import { Component, OnInit, AfterViewInit, ViewChild, Output, EventEmitter, Input, ChangeDetectorRef } from '@angular/core';

import { ModalControl } from '../../../modal-control/modal-control.component';
import { GridColumn, GridBaseOptionExport } from '../../grid-control.model';
import { TranslateService } from '../../../../services/translate/translate.service';
import { NotifyService } from '../../../../services/notify/notify.service';
import { ExcelService } from '../../../../services/excel/excel.service';
import { LoadingService } from '../../../../services/loading/loading.service';
import { ExcelConfig, ExcelTempateColumn } from '../../../../services/excel/excel.model';
import * as hash from 'object-hash';
import { MatRadioChange } from '@angular/material/radio';

@Component({
  selector: 'sd-grid-popup-export-excel',
  templateUrl: './grid-popup-export-excel.component.html',
  styleUrls: ['../../../../assets/css/button.scss',
    '../../../../assets/css/label.scss',
    '../../../../assets/css/scrollbar.scss',
    './grid-popup-export-excel.component.scss']
})
export class GridPopupExportExcelComponent implements OnInit, AfterViewInit {
  private prefix = 'Popup Export Excel';
  newExcelConfig = 'fd8fb5db-a7ad-4398-93ac-cb3350261180';
  @ViewChild('modal') modal: ModalControl;
  @Input() key = '';
  get fullKey() {
    if (!this.key) {
      return '';
    }
    return hash({
      prefix: this.prefix,
      key: this.key
    });
  }
  @Input() columns: GridColumn[] = [];
  @Input() extendColumns: GridColumn[] = [];
  @Input() fileName: string;
  @Input() validator: (fileName: string) => string;
  @Input() exportOption: GridBaseOptionExport;
  excelConfigs: ExcelConfig[] = [];
  @Output() exportByTemplate = new EventEmitter<{ excelConfig: ExcelConfig, excelColumns: ExcelTempateColumn[] }>();
  tabIndex = 0;
  selectedExcelConfig: ExcelConfig = {};
  exportableColumns: GridColumn[] = [];
  selected: {
    [key: string]: boolean
  } = {};
  constructor(
    private ref: ChangeDetectorRef,
    private loadingService: LoadingService,
    private excelService: ExcelService,
    private notifyService: NotifyService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
  }

  ngAfterViewInit() {

  }

  async open() {
    if (!this.fullKey) {
      await this.exportDefault();
      return;
    }
    await this.loadConfiguration();
    this.exportableColumns = [...this.columns, ...(this.extendColumns || [])].filter(e => e.type !== 'image');
    this.selected = {};
    if (!this.selectedExcelConfig?.name) {
      if (this.excelConfigs?.length > 0) {
        this.selectedExcelConfig = this.excelConfigs[0];
      } else {
        this.selectedExcelConfig = {
          name: this.newExcelConfig
        };
      }
    }
    this.modal.open();
    this.ref.detectChanges();
  }

  private generateExcelColumns = (columns: GridColumn[]) => {
    const excelColumns: ExcelTempateColumn[] = [];
    for (const column of columns) {
      if (column.type === 'children') {
        for (const childColumn of column.children) {
          if (childColumn.type !== 'image') {
            excelColumns.push({
              field: childColumn.field,
              title: childColumn.title,
              description: childColumn.description,
              width: childColumn.width
            });
          }
        }
      } else if (column.type !== 'image') {
        excelColumns.push({
          field: column.field,
          title: column.title,
          description: column.description,
          width: column.width
        });
      }
    }
    return excelColumns;
  }

  private initTemplate = async (columns: GridColumn[], fileName?: string) => {
    columns = columns || [];
    const sheets: {
      name: string,
      items: any[],
      fields: string[]
    }[] = [];
    if (Array.isArray(this.exportOption?.sheets)) {
      for (const sheet of this.exportOption.sheets) {
        if (sheet.name && sheet.items && sheet.fields) {
          if (Array.isArray(sheet.items)) {
            sheets.push({
              name: sheet.name,
              items: sheet.items,
              fields: sheet.fields
            });
          } else {
            sheets.push({
              name: sheet.name,
              items: await sheet.items(),
              fields: sheet.fields
            });
          }
        }
      }    }
    return await this.excelService.generateExcelTemplate({
      fileName: fileName || this.fileName,
      columns: this.generateExcelColumns(columns),
      sheets
    });
  }

  private initDefaultTemplate = async () => {
    const extendColumns = this.extendColumns || [];
    const columns = [...this.columns, ...extendColumns];
    return await this.initTemplate(columns);
  }

  generateTemplate = async () => {
    this.loadingService.start();
    const result = await this.initDefaultTemplate().finally(this.loadingService.stop);
    if (result?.filePath) {
      this.excelService.downloadTemplate(result.filePath, `${this.fileName}.xlsx`);
    }
  }

  generateAndUploadTemplate = async (columns: GridColumn[], fileName?: string) => {
    const sheets: {
      name: string,
      items: any[],
      fields: string[]
    }[] = [];
    if (Array.isArray(this.exportOption?.sheets)) {
      for (const sheet of this.exportOption.sheets) {
        if (sheet.name && sheet.items && sheet.fields) {
          if (Array.isArray(sheet.items)) {
            sheets.push({
              name: sheet.name,
              items: sheet.items,
              fields: sheet.fields
            });
          } else {
            sheets.push({
              name: sheet.name,
              items: await sheet.items(),
              fields: sheet.fields
            });
          }
        }
      }
    }
    return await this.excelService.generateAndUploadExcelTemplate(this.fullKey, {
      fileName: fileName || this.fileName,
      columns: this.generateExcelColumns(columns),
      sheets
    });
  }

  onExportByTemplate = (excelConfig: ExcelConfig) => {
    const extendColumns = this.extendColumns || [];
    const columns = [...this.columns, ...extendColumns];
    this.exportByTemplate.emit({
      excelConfig,
      excelColumns: this.generateExcelColumns(columns)
    });
    this.ref.detectChanges();
  }

  uploadTemplate = async () => {
    const excelConfig = await this.excelService.uploadTemplate(this.fullKey, this.validator);
    this.excelConfigs.push({
      name: excelConfig.name,
      fullPath: excelConfig.fullPath,
      removable: true
    });
    this.ref.detectChanges();
  }

  downloadTemplate = (excelConfig: ExcelConfig) => {
    this.excelService.downloadTemplate(excelConfig.fullPath, excelConfig.name);
  }

  removeTemplate = (excelConfig: ExcelConfig) => {
    this.notifyService.confirm(this.translateService.translate('Remove template')).then(async () => {
      this.loadingService.start();
      await this.excelService.remove(this.fullKey, excelConfig.name).finally(this.loadingService.stop);
      const idx = this.excelConfigs.indexOf(excelConfig);
      this.excelConfigs.splice(idx, 1);
      this.ref.detectChanges();
    });
  }

  async loadConfiguration() {
    this.excelConfigs = [];
    this.excelConfigs = await this.excelService.getFiles(this.fullKey);
  }

  exportDefault = async () => {
    this.loadingService.start();
    const result = await this.initDefaultTemplate().finally(this.loadingService.stop);
    const excelColumns: ExcelTempateColumn[] = [];
    const extendColumns = this.extendColumns || [];
    const columns = [...this.columns, ...extendColumns];
    for (const column of columns) {
      if (column.type === 'children') {
        for (const childColumn of column.children) {
          if (childColumn.type !== 'image') {
            excelColumns.push({
              field: childColumn.field,
              title: childColumn.title,
              width: childColumn.width
            });
          }
        }
      } else if (column.type !== 'image') {
        excelColumns.push({
          field: column.field,
          title: column.title,
          width: column.width
        });
      }
    }
    this.exportByTemplate.emit({
      excelConfig: {
        fullPath: result.filePath,
        name: `[${this.fullKey}] Default.xlsx`
      },
      excelColumns
    });
    this.ref.detectChanges();
  }

  newTemplate = () => {
    this.notifyService.confirmWithInput(this.translateService.translate('Enter your template name')).then(async (fileName: string) => {
      this.loadingService.start();
      fileName = fileName + '.xlsx';
      try {
        if (this.validator?.(fileName)) {
          this.notifyService.notify.warning(this.validator(fileName));
          return;
        }
        let exportableColumns = this.exportableColumns.filter(column => this.selected[column.field]);
        if (!exportableColumns.length) {
          exportableColumns = this.exportableColumns;
        }
        const result = await this.generateAndUploadTemplate(exportableColumns, fileName);
        if (result?.filePath) {
          const excelConfig: ExcelConfig = {
            fullPath: result.filePath,
            name: result.fileName,
            removable: true
          };
          this.excelConfigs.push(excelConfig);
          this.onExportByTemplate(excelConfig);
        }
      } finally {
        this.loadingService.stop();
      }
    });
  }

  onChangeRadio = (matRadioChange: MatRadioChange) => {
    const selectedExcelConfig = this.excelConfigs.find(e => e.name === matRadioChange.value);
    if (!selectedExcelConfig) {
      this.selectedExcelConfig = {
        name: this.newExcelConfig
      };
    } else {
      this.selectedExcelConfig = selectedExcelConfig;
    }
    this.exportableColumns = [...this.columns, ...(this.extendColumns || [])].filter(e => e.type !== 'image');
    this.selected = {};
  }
}
